import { useState, type FormEvent } from "react"

import {
  customerTypeChangeApplyRequestV1Schema,
  customerTypeChangePreviewRequestV1Schema,
  customerTypeProfileV1Schema,
  type CustomerTypeChangePreviewResponseV1,
  type CustomerTypeProfileV1,
} from "@impetusos/contracts"

import {
  applyCustomerTypeChange,
  previewCustomerTypeChange,
} from "../transport/index.js"

type CustomerType = CustomerTypeProfileV1["customerType"]

const text = (value: string): string | null => value.trim() || null

export const CustomerTypeProfileEditor = () => {
  const [customerId, setCustomerId] = useState("")
  const [expectedVersion, setExpectedVersion] = useState(1)
  const [branchId, setBranchId] = useState("")
  const [legalEntityId, setLegalEntityId] = useState("")
  const [customerType, setCustomerType] = useState<CustomerType>("residential")
  const [fields, setFields] = useState<Record<string, string | boolean>>({
    occupancyStatus: "unknown",
    billingSameAsService: false,
    poRequired: false,
    billingResponsibility: "property",
    ownerApprovalRequired: true,
    invoiceGrouping: "property",
  })
  const [preview, setPreview] = useState<CustomerTypeChangePreviewResponseV1 | null>(null)
  const [confirmed, setConfirmed] = useState(false)
  const [busy, setBusy] = useState<"preview" | "apply" | null>(null)
  const [notice, setNotice] = useState<string | null>(null)

  const update = (key: string, value: string | boolean) => {
    setFields((current) => ({ ...current, [key]: value }))
    setPreview(null)
    setConfirmed(false)
    setNotice(null)
  }

  const profile = (): CustomerTypeProfileV1 => {
    if (customerType === "residential") {
      return customerTypeProfileV1Schema.parse({
        customerType,
        values: {
          householdName: fields.householdName ?? "",
          occupancyStatus: fields.occupancyStatus ?? "unknown",
          billingSameAsService: fields.billingSameAsService ?? false,
          occupancyDetails: text(String(fields.occupancyDetails ?? "")),
          hoaRestrictions: text(String(fields.hoaRestrictions ?? "")),
          propertyNotes: text(String(fields.propertyNotes ?? "")),
          servicePreferences: text(String(fields.servicePreferences ?? "")),
        },
      })
    }
    if (customerType === "commercial") {
      return customerTypeProfileV1Schema.parse({
        customerType,
        values: {
          legalBusinessName: fields.legalBusinessName ?? "",
          dba: text(String(fields.dba ?? "")),
          industry: fields.industry ?? "",
          apContactId: fields.apContactId ?? "",
          operatingAccessHours: text(String(fields.operatingAccessHours ?? "")),
          poRequired: fields.poRequired ?? false,
          paymentTerms: fields.paymentTerms ?? "",
          taxExemptCertificateMetadata: text(
            String(fields.taxExemptCertificateMetadata ?? "")
          ),
          complianceDocumentReference: text(
            String(fields.complianceDocumentReference ?? "")
          ),
        },
      })
    }
    return customerTypeProfileV1Schema.parse({
      customerType,
      values: {
        managementCompanyName: fields.managementCompanyName ?? "",
        portfolioIdentifier: fields.portfolioIdentifier ?? "",
        billingResponsibility: fields.billingResponsibility ?? "property",
        authorizationLimitMinor: Number(fields.authorizationLimitMinor ?? 0),
        ownerApprovalRequired: fields.ownerApprovalRequired ?? true,
        tenantNoticeRules: text(String(fields.tenantNoticeRules ?? "")),
        invoiceGrouping: fields.invoiceGrouping ?? "property",
      },
    })
  }

  const baseRequest = () => ({
    requestVersion: 1 as const,
    correlationId: crypto.randomUUID(),
    idempotencyKey: `customer-type-${crypto.randomUUID()}`,
    customerId,
    expectedVersion,
    branchId,
    legalEntityId,
    profile: profile(),
  })

  const reviewImpact = async () => {
    setBusy("preview")
    setNotice(null)
    try {
      const result = await previewCustomerTypeChange(
        customerTypeChangePreviewRequestV1Schema.parse(baseRequest())
      )
      setPreview(result)
      setConfirmed(false)
      if (result.status === "conflict") {
        setNotice("The Customer changed. Enter the current version and preview again.")
      } else if (result.status !== "reviewed") {
        setNotice("The authoritative impact preview was not available.")
      }
    } catch {
      setNotice("Complete the Customer scope and the selected type profile.")
    } finally {
      setBusy(null)
    }
  }

  const apply = async (event: FormEvent) => {
    event.preventDefault()
    if (
      preview?.status !== "reviewed" ||
      preview.impactFingerprint === null ||
      preview.currentVersion === null ||
      !confirmed
    ) {
      setNotice("Preview and confirm the current impact before applying this revision.")
      return
    }
    setBusy("apply")
    setNotice(null)
    try {
      const result = await applyCustomerTypeChange(
        customerTypeChangeApplyRequestV1Schema.parse({
          ...baseRequest(),
          expectedVersion: preview.currentVersion,
          impactFingerprint: preview.impactFingerprint,
          typeRevisionId: crypto.randomUUID(),
        })
      )
      if (result.status === "confirmed" && result.version !== null) {
        setExpectedVersion(result.version)
        setPreview(null)
        setConfirmed(false)
        setNotice(
          `Type profile ${result.typeRevisionId ?? ""} was recorded at Customer version ${result.version}.`
        )
      } else if (result.status === "conflict") {
        setPreview(null)
        setConfirmed(false)
        setNotice("The Customer or impact changed. Preview again before applying.")
      } else {
        setNotice("The authoritative service did not confirm the revision.")
      }
    } catch {
      setNotice("The type revision could not be confirmed. Review the fields and retry.")
    } finally {
      setBusy(null)
    }
  }

  return (
    <section className="customer-wizard" id="customer-profiles" aria-labelledby="customer-profile-title">
      <div className="wizard-heading">
        <div><p className="eyebrow">Versioned profile</p><h2 id="customer-profile-title">Customer type and conditional details</h2></div>
        <span className="step-badge">Impact preview required</span>
      </div>
      {notice && <div className="notice" role="status"><span aria-hidden="true">!</span><p>{notice}</p></div>}
      <form onSubmit={(event) => { void apply(event) }}>
        <fieldset>
          <legend>Customer and owning scope</legend>
          <label><span>Customer ID</span><input required value={customerId} onChange={(event) => { setCustomerId(event.target.value); setPreview(null) }} /></label>
          <label><span>Current version</span><input required min="1" type="number" value={expectedVersion} onChange={(event) => { const nextVersion = event.currentTarget.valueAsNumber; setExpectedVersion(Number.isFinite(nextVersion) && nextVersion >= 1 ? nextVersion : 1); setPreview(null) }} /></label>
          <label><span>Owning Branch ID</span><input required value={branchId} onChange={(event) => { setBranchId(event.target.value); setPreview(null) }} /></label>
          <label><span>Legal Entity ID</span><input required value={legalEntityId} onChange={(event) => { setLegalEntityId(event.target.value); setPreview(null) }} /></label>
          <label><span>Target type</span><select value={customerType} onChange={(event) => { setCustomerType(event.target.value as CustomerType); setPreview(null); setConfirmed(false) }}><option value="residential">Residential</option><option value="commercial">Commercial / organization</option><option value="property_manager">Property manager</option></select></label>
        </fieldset>

        {customerType === "residential" && <fieldset><legend>Residential profile</legend>
          <label><span>Household or individual name</span><input required value={String(fields.householdName ?? "")} onChange={(event) => update("householdName", event.target.value)} /></label>
          <label><span>Occupancy</span><select value={String(fields.occupancyStatus ?? "unknown")} onChange={(event) => update("occupancyStatus", event.target.value)}><option value="owner">Owner</option><option value="tenant">Tenant</option><option value="other">Other</option><option value="unknown">Unknown</option></select></label>
          <label className="check-field"><input type="checkbox" checked={Boolean(fields.billingSameAsService)} onChange={(event) => update("billingSameAsService", event.target.checked)} /><span>Billing address is the service address</span></label>
          <label><span>Occupancy details</span><input value={String(fields.occupancyDetails ?? "")} onChange={(event) => update("occupancyDetails", event.target.value)} /></label>
          <label><span>HOA restrictions</span><input value={String(fields.hoaRestrictions ?? "")} onChange={(event) => update("hoaRestrictions", event.target.value)} /></label>
          <label><span>Property notes</span><textarea value={String(fields.propertyNotes ?? "")} onChange={(event) => update("propertyNotes", event.target.value)} /></label>
          <label><span>Service preferences</span><input value={String(fields.servicePreferences ?? "")} onChange={(event) => update("servicePreferences", event.target.value)} /></label>
        </fieldset>}

        {customerType === "commercial" && <fieldset><legend>Commercial / organization profile</legend>
          <label><span>Legal business name</span><input required value={String(fields.legalBusinessName ?? "")} onChange={(event) => update("legalBusinessName", event.target.value)} /></label>
          <label><span>DBA</span><input value={String(fields.dba ?? "")} onChange={(event) => update("dba", event.target.value)} /></label>
          <label><span>Industry</span><input required value={String(fields.industry ?? "")} onChange={(event) => update("industry", event.target.value)} /></label>
          <label><span>AP Contact ID</span><input required value={String(fields.apContactId ?? "")} onChange={(event) => update("apContactId", event.target.value)} /></label>
          <label><span>Operating / access hours</span><input value={String(fields.operatingAccessHours ?? "")} onChange={(event) => update("operatingAccessHours", event.target.value)} /></label>
          <label className="check-field"><input type="checkbox" checked={Boolean(fields.poRequired)} onChange={(event) => update("poRequired", event.target.checked)} /><span>Purchase order required</span></label>
          <label><span>Payment terms</span><input required value={String(fields.paymentTerms ?? "")} onChange={(event) => update("paymentTerms", event.target.value)} /></label>
          <label><span>Tax certificate metadata</span><input value={String(fields.taxExemptCertificateMetadata ?? "")} onChange={(event) => update("taxExemptCertificateMetadata", event.target.value)} /></label>
          <label><span>Compliance document reference</span><input value={String(fields.complianceDocumentReference ?? "")} onChange={(event) => update("complianceDocumentReference", event.target.value)} /></label>
        </fieldset>}

        {customerType === "property_manager" && <fieldset><legend>Property manager profile</legend>
          <label><span>Management company</span><input required value={String(fields.managementCompanyName ?? "")} onChange={(event) => update("managementCompanyName", event.target.value)} /></label>
          <label><span>Portfolio identifier</span><input required value={String(fields.portfolioIdentifier ?? "")} onChange={(event) => update("portfolioIdentifier", event.target.value)} /></label>
          <label><span>Billing responsibility</span><select value={String(fields.billingResponsibility ?? "property")} onChange={(event) => update("billingResponsibility", event.target.value)}><option value="central">Central</option><option value="property">Per property</option></select></label>
          <label><span>Authorization limit (minor units)</span><input min="0" type="number" value={String(fields.authorizationLimitMinor ?? "0")} onChange={(event) => update("authorizationLimitMinor", event.target.value)} /></label>
          <label className="check-field"><input type="checkbox" checked={Boolean(fields.ownerApprovalRequired)} onChange={(event) => update("ownerApprovalRequired", event.target.checked)} /><span>Owner approval required</span></label>
          <label><span>Tenant notice rules</span><input value={String(fields.tenantNoticeRules ?? "")} onChange={(event) => update("tenantNoticeRules", event.target.value)} /></label>
          <label><span>Invoice grouping</span><select value={String(fields.invoiceGrouping ?? "property")} onChange={(event) => update("invoiceGrouping", event.target.value)}><option value="central">Central</option><option value="property">Property</option><option value="separate">Separate</option></select></label>
        </fieldset>}

        <div className="duplicate-panel" aria-live="polite">
          <button type="button" className="secondary-button" disabled={busy !== null} onClick={() => { void reviewImpact() }}>{busy === "preview" ? "Reviewing…" : "Preview type impact"}</button>
          {preview?.status === "reviewed" && <div className="duplicate-warning"><strong>{preview.currentType} → {preview.targetType}</strong><p>{preview.fieldsIntroduced.length} fields introduced; {preview.fieldsRetired.length} become historical. Contacts, locations, billing profiles, Branch grants, and all prior type revisions are retained.</p><label className="check-field"><input type="checkbox" checked={confirmed} onChange={(event) => setConfirmed(event.target.checked)} /><span>I reviewed the impact and intend to record this revision.</span></label></div>}
        </div>
        <div className="wizard-actions"><p>Frappe recomputes the preview against the locked Customer and current relationships before commit.</p><button className="primary-button" type="submit" disabled={busy !== null || !confirmed}>{busy === "apply" ? "Awaiting confirmation…" : "Apply type revision"}</button></div>
      </form>
    </section>
  )
}
